import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";

import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Moon, Clock, CheckCircle, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { useGrowthStandup, StandupTask, TaskForStandup } from "@/hooks/useGrowthStandup";
import { useTaskPopup } from "@/contexts/TaskPopupContext";
import { useSlackApi } from "@/hooks/useSlackApi";
import { useTaskCreationSettings } from "@/hooks/useTaskCreationSettings";
import { useBrandManager } from "@/hooks/useBrandManager";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { StandupTaskItem } from "./StandupTaskItem";
import { format } from "date-fns";
import { TimeTrackingSource } from "@/hooks/useTimeTracking";

// Type for the hook return value
type StandupHookReturn = ReturnType<typeof useGrowthStandup>;

interface DayEndDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  standupHook?: StandupHookReturn;
  /** When true, render content inline (no modal). For holdco views. */
  embedded?: boolean;
}

export const DayEndDialog: React.FC<DayEndDialogProps> = ({
  open = true,
  onOpenChange,
  standupHook: externalHook,
  embedded = false,
}) => {
  // Use external hook if provided, otherwise create internal one (for backwards compatibility)
  const internalHook = useGrowthStandup();
  const { 
    standupTasks, 
    isLoadingStandup, 
    updateTaskStatus, 
    updateTimeSpent,
    removeFromStandup,
    refetch 
  } = externalHook || internalHook;
  const { openTaskPopup } = useTaskPopup();
  const { sendNotification, isLoading: slackLoading } = useSlackApi();
  const { settings } = useTaskCreationSettings();
  const { currentBrandId } = useBrandManager();
  const { user } = useAuth();
  
  const [pendingChanges, setPendingChanges] = useState<Map<string, { time?: number; status?: string }>>(new Map());
  const [isSaving, setIsSaving] = useState(false);

  // Refetch data when dialog opens to ensure fresh DB values
  useEffect(() => {
    if (open) {
      console.log('[DayEndDialog] Dialog opened - triggering refetch for fresh DB data');
      refetch();
    }
  }, [open]);

  // Calculate stats
  const totalTasks = standupTasks.length;
  const completedTasks = standupTasks.filter(t => t.task_status === 'done' || t.task_status === 'completed').length;
  const totalTimeSpent = standupTasks.reduce((acc, t) => acc + (t.time_spent || 0), 0);

  // Check for tasks marked done without time logged
  const tasksNeedingTime = standupTasks.filter(t => 
    (t.task_status === 'done' || t.task_status === 'completed') && 
    (!t.time_spent || t.time_spent === 0)
  );

  // Handle time change - passes increment and source to DB-first updateTimeSpent
  const handleTimeChange = async (taskId: string, minutesToAdd: number, source: TimeTrackingSource) => {
    const task = standupTasks.find(t => t.task_id === taskId);
    if (task) {
      await updateTimeSpent(taskId, task.task_type, minutesToAdd, source);
    }
  };

  const handleStatusChange = async (taskId: string, status: string) => {
    const task = standupTasks.find(t => t.task_id === taskId);
    if (task) {
      await updateTaskStatus(taskId, task.task_type, status);
    }
  };

  const handleRemove = async (taskId: string) => {
    const task = standupTasks.find(t => t.task_id === taskId);
    if (task) {
      await removeFromStandup(task.id);
    }
  };

  const handleTaskClick = (task: any) => {
    // Transform to expected format and open popup
    const transformedTask = {
      id: task.task_id,
      title: task.task_title,
      name: task.task_title,
      status: task.task_status,
      task_status: task.task_status,
      time_spent: task.time_spent,
      brand_id: task.brand_id,
      brands: {
        name: task.brand_name,
        slug: task.brand_slug,
      },
      type: task.task_type,
    };
    openTaskPopup(transformedTask, task.task_type);
  };

  const formatTimeString = (minutes: number | undefined | null): string => {
    if (!minutes || minutes === 0) return '-';
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  const formatStatusLabel = (status: string): string => {
    switch (status) {
      case 'in_progress': return 'In Progress';
      case 'done': 
      case 'completed': return 'Done';
      case 'pending': return 'Pending';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const formatForSlackDayEnd = (): string => {
    const today = format(new Date(), 'MMMM d, yyyy');
    const userName = user?.user_metadata?.full_name || user?.email || 'User';
    
    let message = `*Day End - ${today} - ${userName}*\n\n`;

    standupTasks.forEach(task => {
      const dueDate = task.original_due_date ? format(new Date(task.original_due_date), 'MMM d') : '-';
      const estTime = formatTimeString((task as any).time_estimated);
      const spentTime = formatTimeString(task.time_spent);
      const status = formatStatusLabel(task.task_status || 'pending');
      
      message += `${task.brand_name} - ${task.task_title} - Due: ${dueDate} - Est: ${estTime} - Spent: ${spentTime} - ${status}\n`;
    });

    const totalSpent = standupTasks.reduce((acc, t) => acc + (t.time_spent || 0), 0);
    
    message += `\n*Total: ${standupTasks.length} tasks | ${completedTasks} completed | ${formatTimeString(totalSpent)} logged*`;

    return message;
  };

  const handleSaveAndComplete = async () => {
    setIsSaving(true);
    try {
      // Update submission record with Day End timestamp
      const today = format(new Date(), 'yyyy-MM-dd');
      
      try {
        const { error: submissionError } = await supabase
          .from('os_growth_standup_submissions')
          .update({
            day_end_at: new Date().toISOString(),
            day_end_task_count: standupTasks.length,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user?.id)
          .eq('submission_date', today);
        
        if (submissionError) {
          console.error('Error updating submission:', submissionError);
        }
      } catch (err) {
        console.error('Error tracking day end:', err);
      }
      
      // Send to Slack - use configured report brand, fallback to current brand
      const typedSettings = settings as typeof settings & { growth_standup_report_brand_id?: string | null };
      const reportBrandId = typedSettings?.growth_standup_report_brand_id || currentBrandId;
      
      if (reportBrandId && standupTasks.length > 0) {
        const message = formatForSlackDayEnd();
        await sendNotification(message, undefined, reportBrandId);
      }
      
      refetch();
      if (!embedded) onOpenChange?.(false);
    } finally {
      setIsSaving(false);
    }
  };

  const formatTimeDisplay = (minutes: number) => {
    if (minutes === 0) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${mins}m`;
  };

  const headerContent = (
    <div className="flex items-center gap-2">
      <Moon className="h-5 w-5 text-indigo-500" />
      Day End
      <span className="text-sm font-normal text-muted-foreground">
        {format(new Date(), 'EEEE, MMMM d, yyyy')}
      </span>
    </div>
  );

  const bodyContent = (
    <>
        {/* Stats bar */}
        <div className="flex items-center gap-4 py-2">
          <Badge variant="outline" className="gap-1">
            <CheckCircle className="h-3 w-3" />
            {completedTasks}/{totalTasks} completed
          </Badge>
          <Badge variant="outline" className="gap-1">
            <Clock className="h-3 w-3" />
            {formatTimeDisplay(totalTimeSpent)} logged
          </Badge>
          <TooltipProvider delayDuration={300} skipDelayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => refetch()}
                  disabled={isLoadingStandup}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isLoadingStandup ? 'animate-spin' : ''}`} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs py-1 px-2">
                Refresh
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Warning for tasks marked done without time */}
        {tasksNeedingTime.length > 0 && (
          <Alert variant="destructive" className="py-2">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              {tasksNeedingTime.length} task(s) marked as done need time logged
            </AlertDescription>
          </Alert>
        )}

        <div className="flex-1 -mx-6 px-6 overflow-y-auto min-h-0">
          {/* Loading state */}
          {isLoadingStandup && (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin mb-3 text-primary" />
              <p className="text-lg font-medium">Retrieving Tasks</p>
              <p className="text-sm">Please wait while we load your standup...</p>
            </div>
          )}

          {!isLoadingStandup && (
            <div className="space-y-2 pb-4">
              {standupTasks.map(task => (
                <StandupTaskItem
                  key={task.id}
                  task={{
                    id: task.task_id,
                    title: task.task_title || 'Unknown Task',
                    type: task.task_type,
                    status: task.task_status || 'pending',
                    due_date: task.original_due_date,
                    brand_name: task.brand_name || 'Unknown Brand',
                  }}
                  showTimeTracker
                  allowManualTimeAdjustment={settings?.allow_manual_time_adjustment ?? true}
                  timeSpent={task.time_spent || 0}
                  onTimeChange={handleTimeChange}
                  onStatusChange={handleStatusChange}
                  onRemove={() => handleRemove(task.task_id)}
                  onClick={() => handleTaskClick(task)}
                />
              ))}

              {standupTasks.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Moon className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p className="text-lg font-medium">No tasks in today's standup</p>
                  <p className="text-sm">Use "Day Start" to add tasks for today</p>
                </div>
              )}
            </div>
          )}
        </div>

        <Separator />

        <div className="flex flex-row justify-end gap-2 pt-4">
          {!embedded && (
            <Button variant="outline" onClick={() => onOpenChange?.(false)}>
              Close
            </Button>
          )}
          <Button 
            onClick={handleSaveAndComplete}
            disabled={isLoadingStandup || isSaving || slackLoading}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Save & Complete Day
          </Button>
        </div>
    </>
  );

  if (embedded) {
    return (
      <div className="flex flex-col rounded-lg border bg-card text-card-foreground max-h-[70vh] overflow-hidden">
        <div className="px-6 pt-6 pb-0 flex-shrink-0">{headerContent}</div>
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden px-6">{bodyContent}</div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange ?? (() => {})}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitleWithBug
            popupName="Day End - Growth Standup"
            popupContext="/my-tasks-all-brands"
            isOpen={open}
          >
            {headerContent}
          </DialogTitleWithBug>
        </DialogHeader>
        {bodyContent}
      </DialogContent>
    </Dialog>
  );
};
